import React from 'react';
import { getFindingEvidenceColumns } from './findingEvidence';
import { Helpers, RULE_LABELS } from '@engine/index';
import { t, getMonthShort, getCurrentLang } from '../lib/i18n';

function isLegacyInvoiceGapRow(row) {
    return typeof row?.name === 'string' && row.name.trim().startsWith('Luka:') && row.accNum !== undefined && row.ub !== undefined;
}

function normalizeInvoiceGapRow(row) {
    if (row.fromInvoice !== undefined && row.toInvoice !== undefined) return row;
    const match = typeof row.name === 'string' ? row.name.match(/Luka:\s*(\d+)\s*→\s*(\d+)/) : null;
    const missingMatch = typeof row.name === 'string' ? row.name.match(/\((?:\d+)\s+[^:]+:\s*([^)]+)\)/) : null;
    return {
        fromInvoice: match ? Number(match[1]) : row.accNum,
        toInvoice: match ? Number(match[2]) : '',
        missingCount: row.ub,
        missingInvoices: missingMatch ? missingMatch[1] : row.name,
    };
}

/**
 * EvidenceTable — Renders audit evidence with sticky columns, monthly data,
 * account names. Supports saldo, resultat, and monthly_sum display modes.
 *
 * Extracted from index.html (previously inline ~lines 689-799).
 *
 * @param {{ rows: Array, type: string }} props
 *   - rows: evidence row objects from audit engine
 *   - type: 'saldo' | 'resultat' | 'monthly_sum' — determines column layout
 */
export default function EvidenceTable({ rows, type, grouped = false, ruleId }) {
    if (!rows || rows.length === 0) return null;
    if (type === 'coverage' || rows.every(r => r.ruleId && r.reason)) return (
        <ul className="mt-4 space-y-2 text-sm">{rows.map(r => <li key={r.ruleId}>
            <strong>{t(RULE_LABELS[r.ruleId] || r.ruleId)}</strong>: {t(['saldo', 'resultat', 'both', 'transactions', 'context'].includes(r.reason) ? 'coverage_reason_' + r.reason : r.reason)}
        </li>)}</ul>
    );
    const semantic = getFindingEvidenceColumns(ruleId, rows);
    if (semantic) return (
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm text-left" data-finding-evidence={ruleId}>
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase">
                    <tr>{semantic.map(column => <th key={column.key} className="px-4 py-2" scope="col">{column.label}</th>)}</tr>
                </thead>
                <tbody>{rows.map((row,index) => <tr key={index} className="border-t border-slate-100 dark:border-slate-700">
                    {semantic.map(column => <td key={column.key} className="px-4 py-2 whitespace-normal break-words">{column.value(row)}</td>)}
                </tr>)}</tbody>
            </table>
        </div>
    );
    const missingLabel = t('no_data');
    const voucherLabel = getCurrentLang() === 'pl' ? 'Dokument' : 'Bilag';
    const money = value => Number.isFinite(value) ? Helpers.formatMoney(value) : missingLabel;
    const monthlyBalance = row => row.accNum >= 1000 && row.accNum <= 2999;
    const hasMonths = rows.some(row => row.months != null && typeof row.months === 'object' && !Array.isArray(row.months) && Object.keys(row.months).length > 0);
    const monthNumber = key => /^\d+$/.test(String(key)) ? Number(key)
        : ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'].indexOf(String(key).toLowerCase()) + 1;
    const monthKey = key => {
        const number = monthNumber(key);
        return number >= 1 && number <= 12 ? String(number) : key;
    };
    const monthKeys = [...new Set(rows.flatMap(row => Object.keys(row.months || {}).map(monthKey)))].sort((a, b) =>
        (monthNumber(a) || 13) - (monthNumber(b) || 13));
    const monthLabel = key => getMonthShort()[monthNumber(key) - 1] || key;
    const monthValue = (row, key) => Object.entries(row.months || {}).find(([m]) => monthKey(m) === key)?.[1];
    const hasBalanceRows = rows.some(monthlyBalance);
    const hasSumRows = rows.some(row => !monthlyBalance(row));
    const monthlyTotalLabel = hasBalanceRows && hasSumRows ? t('sum') + ' / ' + t('saldoUB') : hasBalanceRows ? t('saldoUB') : t('sum');
    const isIbUbContinuity = rows.some(row => ['currentIb', 'previousUb', 'diff'].some(key => Object.prototype.hasOwnProperty.call(row, key)));
    const isInvoiceGap = type === 'invoice_gap' || rows.some(r => r.fromInvoice !== undefined && r.toInvoice !== undefined && r.missingCount !== undefined) || rows.some(isLegacyInvoiceGapRow);

    if (!grouped && type !== 'monthly_sum' && !isInvoiceGap && rows.some(row => row.groupId != null && row.evidenceStrength != null && row.voucherId != null)) {
        const groups = new Map();
        const sections = [];
        for (const row of rows) {
            if (row.groupId == null || row.evidenceStrength == null || row.voucherId == null) {
                sections.push({ rows: [row] });
                continue;
            }
            const key = String(row.groupId);
            if (!groups.has(key)) {
                const section = { groupId: row.groupId, rows: [] };
                groups.set(key, section);
                sections.push(section);
            }
            groups.get(key).rows.push(row);
        }
        return <div className="mt-4 space-y-3">{sections.map((section, index) => section.groupId == null
            ? <EvidenceTable key={'row-' + index} rows={section.rows} type={type} grouped />
            : <details key={'group-' + section.groupId} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
                <summary className="cursor-pointer px-4 py-3 text-sm font-semibold break-words">
                    {t(section.rows.every(row => row.evidenceStrength === 'weak') ? 'duplicates_weak_title' : 'rule_duplicates')} #{section.groupId} ({section.rows.length}): {voucherLabel} {Array.from(new Set(section.rows.map(row => row.voucherId))).slice(0, 3).map(id => '#' + id).join(', ')}{new Set(section.rows.map(row => row.voucherId)).size > 3 ? ' ...' : ''}
                </summary>
                <EvidenceTable rows={section.rows} type={type} grouped />
            </details>)}</div>;
    }

    // Determines if we should use sticky column logic (only for month-based data tables)
    // Note: Monthly summary (type 'monthly_sum') is a small vertical table, so no sticky needed.
    const useSticky = hasMonths && type !== 'monthly_sum';

    // Container class: applies sticky-table-container only if we need sticky columns
    const containerClass = `mt-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm ${useSticky ? 'sticky-table-container' : 'overflow-x-auto'}`;

    return (
        <div className={containerClass}>
            {hasMonths && <p className="px-4 py-2 text-xs text-slate-500">{t('evidence_monthly_movements')}</p>}
            <table className={`w-full text-sm text-left ${useSticky ? 'border-separate border-spacing-0' : ''}`}>
                <thead className={`bg-slate-50 dark:bg-slate-800 text-xs uppercase font-semibold ${useSticky ? '' : ''}`}>
                    <tr>
                        {type === 'monthly_sum' ? (
                            <>
                                <th className="px-4 py-2">{t('month')}</th>
                                <th className="px-4 py-2 text-right">{t('sumVertical')}</th>
                                <th className="px-4 py-2 text-center">{t('status')}</th>
                            </>
                        ) : isInvoiceGap ? (
                            <>
                                <th className="px-4 py-2 text-right">{t('fromInvoice')}</th>
                                <th className="px-4 py-2 text-right">{t('toInvoice')}</th>
                                <th className="px-4 py-2 text-right">{t('missingCount')}</th>
                                <th className="px-4 py-2">{t('missingInvoices')}</th>
                            </>
                        ) : (
                            <>
                                {/* HEADERS */}
                                <th className={`px-4 py-2 ${useSticky ? 'sticky-cell sticky-col-1' : ''}`}>{t('account')}</th>
                                <th className={`px-4 py-2 ${useSticky ? 'sticky-cell sticky-col-2' : ''}`}>{t('accountName')}</th>

                                {hasMonths ? (
                                    <>
                                        {monthKeys.map(m => (
                                            <th key={m} className="px-2 py-2 text-right text-[10px] bg-slate-50 dark:bg-slate-800">{monthLabel(m)}</th>
                                        ))}
                                        <th className={`px-4 py-2 text-right font-bold border-l dark:border-slate-700 ${useSticky ? 'sticky-cell sticky-col-last' : ''}`}>
                                            {monthlyTotalLabel}
                                        </th>
                                    </>
                                ) : isIbUbContinuity ? (
                                    <>
                                        <th className="px-4 py-2 text-right">{t('currentIb')}</th>
                                        <th className="px-4 py-2 text-right">{t('previousUb')}</th>
                                        <th className="px-4 py-2 text-right">{t('diff')}</th>
                                    </>
                                ) : type === 'saldo' ? (
                                    <>
                                        <th className="px-4 py-2 text-right">{t('saldoUB')}</th>
                                        <th className="px-4 py-2 text-right">{t('change')}</th>
                                    </>
                                ) : (
                                    <th className="px-4 py-2 text-right">{t('sum')}</th>
                                )}
                            </>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {rows.map((r, i) => {
                        if (type === 'monthly_sum') {
                            return (
                                <tr key={i} className={`font-mono ${r.isError ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-white dark:bg-slate-900'}`}>
                                    <td className="px-4 py-2 font-bold">{r.month}</td>
                                    <td className="px-4 py-2 text-right">{money(r.sum)}</td>
                                    <td className="px-4 py-2 text-center text-xs">{!Number.isFinite(r.sum) ? missingLabel : r.isError ? t('wrongSign') : "OK"}</td>
                                </tr>
                            );
                        }

                        if (isInvoiceGap) {
                            const gap = normalizeInvoiceGapRow(r);
                            return (
                                <tr key={i} className="bg-white dark:bg-slate-900 font-mono hover:bg-slate-50 dark:hover:bg-slate-800">
                                    <td className="px-4 py-2 text-right">{gap.fromInvoice}</td>
                                    <td className="px-4 py-2 text-right">{gap.toInvoice}</td>
                                    <td className="px-4 py-2 text-right font-semibold">{gap.missingCount}</td>
                                    <td className="px-4 py-2 whitespace-normal break-words text-xs">{gap.missingInvoices}</td>
                                </tr>
                            );
                        }

                        return (
                            <tr key={i} className="bg-white dark:bg-slate-900 font-mono hover:bg-slate-50 dark:hover:bg-slate-800">
                                {/* ACCOUNT ID CELL */}
                                <td className={`px-4 py-2 whitespace-nowrap ${useSticky ? 'sticky-cell sticky-col-1' : ''}`}>
                                    {/* Wiersze projektowe i bilagowe nie mają konta (accNum 0): myślnik zamiast zera (11.10.2026). */}
                                    {r.id ?? (r.accNum ? r.accNum : '—')}
                                </td>

                                {/* NAME CELL */}
                                <td className={`px-4 py-2 ${useSticky ? 'sticky-cell sticky-col-2 truncate max-w-[200px]' : 'max-w-[400px]'}`}>
                                    {r.voucherId != null && <span className="block text-xs font-semibold">{voucherLabel} #{r.voucherId}</span>}
                                    <span className={useSticky ? "truncate-text" : "whitespace-normal break-words text-xs"} title={r.accName || r.name}>{r.name}</span>
                                </td>

                                {hasMonths ? (
                                    <>
                                        {/* MONTH CELLS (SCROLLABLE) */}
                                        {monthlyBalance(r) && (!r.months || Object.keys(r.months).length === 0) ? <td colSpan={monthKeys.length} className="px-2 py-2 text-xs text-slate-500">{t('evidence_no_monthly_breakdown')}</td> : monthKeys.map(m => {
                                            const val = monthValue(r, m);
                                            const isAnomaly = r.anomalousMonths?.some(key => monthKey(key) === m);
                                            const cellClass = isAnomaly
                                                ? 'bg-red-100 text-red-700 font-bold border border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700'
                                                : (val === 0 ? 'text-slate-300 dark:text-slate-700' : '');

                                            return (
                                                <td key={m} className={`px-2 py-2 text-right whitespace-nowrap ${cellClass}`}>
                                                    {money(val).replace(' NOK', '')}
                                                </td>
                                            );
                                        })}
                                        {/* SUM CELL (STICKY RIGHT) — for balance accounts (1xxx/2xxx) show UB if available and != sum */}
                                        <td className={`px-4 py-2 text-right font-bold border-l dark:border-slate-700 ${useSticky ? 'sticky-cell sticky-col-last' : ''}`}>
                                            {monthlyBalance(r)
                                                ? <span title={t('saldoUB') + '; ' + t('change') + ': ' + money(r.change ?? r.sum)}>{money(r.ub)}</span>
                                                : <span title={t('sum')}>{money(r.sum)}</span>}
                                        </td>
                                    </>
                                ) : isIbUbContinuity ? (
                                    <>
                                        <td className="px-4 py-2 text-right">{money(r.currentIb)}</td>
                                        <td className="px-4 py-2 text-right">{money(r.previousUb)}</td>
                                        <td className={`px-4 py-2 text-right font-semibold ${r.diff < 0 ? 'text-red-500' : ''}`}>{money(r.diff)}</td>
                                    </>
                                ) : type === 'saldo' ? (
                                    <>
                                        <td className={`px-4 py-2 text-right ${r.ub < 0 ? 'text-red-500' : ''}`}>{money(r.ub)}</td>
                                        <td className="px-4 py-2 text-right">{!Number.isFinite(r.change) && r.changeMissingReason === 'no_comparison' ? t('bank_change_no_comparison') : money(r.change)}</td>
                                    </>
                                ) : (
                                    <td className="px-4 py-2 text-right">{money(r.ub ?? r.sum)}</td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
